import { router, usePage } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, Download, Loader2, Trash2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { toast } from "sonner"
import { effectiveFrameFormat, frameFormatClasses } from "@/lib/frame-format"
import {
  activatePostProductionVersion,
  deletePostProductionVersion,
  fetchShotPostProductionVersions,
  resetPostProductionVersions,
  type PostProductionOriginalRow,
  type PostProductionVersionRow,
} from "@/lib/workspace-api"
import { downloadImage } from "@/lib/download-image"
import { useInitialLoadReporter } from "@/components/shot-panel/use-initial-load-reporter"
import { cn } from "@/lib/utils"
import { activeShotJob, useReloadOnJobFinish } from "@/lib/shot-jobs"
import { statusDetailLabel } from "@/components/jobs/job-elapsed-timer"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import type { GenerationJobRow, WorkspaceBaseProps } from "@/types/workspace"
import { confirm } from "@/lib/confirm"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { type CompareState, VersionCompareControls } from "@/components/review/review-parts"
import { type ReviewMediaVersion, VersionCompare } from "@/components/review/shot-review-media"
import { VersionCompareSlot } from "@/components/review/version-compare-slot"

const EMPTY_WORKSPACE_JOBS: GenerationJobRow[] = []
const POST_PRODUCTION_JOB_KINDS = ["topaz_post_production"] as const

const PREVIEW_CLICK_MS = 250
// У оригинала нет строки обработки — в сравнении он идёт под этим id.
const ORIGINAL_ID = -1

/** Строка обработки или оригинал для общего сравнения (VersionCompare); сравнивать можно только готовые. */
function compareVersion(
  row: PostProductionVersionRow | PostProductionOriginalRow,
  id: number,
  number: number,
  label: string,
): ReviewMediaVersion | null {
  if (!row.preview_url) return null
  return {
    id,
    number,
    label,
    kind: "video",
    active: row.active,
    media_url: row.preview_url,
    thumb_url: thumbParts(row).thumbImg,
  }
}

function useDelayedPreviewOpen() {
  const [open, setOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])
  useEffect(() => cancel, [cancel])
  const schedule = useCallback(() => {
    cancel()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setOpen(true)
    }, PREVIEW_CLICK_MS)
  }, [cancel])
  return { open, setOpen, schedule, cancel }
}

function PostProductionPreview({
  previewUrl,
  stillUrl,
  aspectClass,
  title,
  active,
  activeLabel,
  empty,
  cancelRef,
  overlay,
}: {
  previewUrl: string | null
  stillUrl: string | null
  aspectClass: string
  title?: string
  active: boolean
  activeLabel: string
  empty: ReactNode
  cancelRef: { current: () => void }
  /** Выбор для сравнения поверх превью. */
  overlay?: ReactNode
}) {
  const { open, setOpen, schedule, cancel } = useDelayedPreviewOpen()
  useEffect(() => {
    cancelRef.current = cancel
    return () => {
      cancelRef.current = () => {}
    }
  }, [cancel, cancelRef])

  return (
    <>
      <div
        className={cn(
          "relative w-full overflow-hidden rounded-t-lg dark:bg-ui-bg-elevated light:bg-clay-2",
          previewUrl && "cursor-pointer",
          aspectClass,
        )}
        title={title}
        onClick={previewUrl ? schedule : undefined}
      >
        {previewUrl ? (
          <>
            {stillUrl ? (
              <img src={stillUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <video
                src={previewUrl}
                className="pointer-events-none h-full w-full object-cover"
                preload="metadata"
                muted
                playsInline
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-transparent transition-colors hover:bg-black/25">
              <PlayBadge />
            </div>
          </>
        ) : (
          empty
        )}
        {active ? (
          <span className="absolute top-1.5 left-1.5 z-[3] rounded bg-ui-accent/90 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-ui-on-accent uppercase">
            {activeLabel}
          </span>
        ) : null}
        {overlay}
      </div>
      {previewUrl ? (
        <ImageLightboxDialog open={open} onOpenChange={setOpen} url={null} videoUrl={previewUrl} nested />
      ) : null}
    </>
  )
}

function thumbParts(row: {
  first_frame_url?: string | null
  first_frame_thumb_url?: string | null
  preview_thumb_url: string | null
}) {
  const poster = row.first_frame_url ?? row.first_frame_thumb_url ?? row.preview_thumb_url ?? undefined
  const thumbImg = row.first_frame_url ?? row.first_frame_thumb_url ?? row.preview_thumb_url ?? null
  return { thumbImg, poster }
}

function isSameVersionRow(a: PostProductionVersionRow, b: PostProductionVersionRow) {
  return (
    a.status === b.status &&
    a.progress === b.progress &&
    a.active === b.active &&
    a.error_message === b.error_message &&
    a.preview_url === b.preview_url &&
    a.preview_thumb_url === b.preview_thumb_url &&
    a.first_frame_url === b.first_frame_url &&
    a.first_frame_thumb_url === b.first_frame_thumb_url &&
    a.download_url === b.download_url &&
    a.result_shot_media_version_id === b.result_shot_media_version_id
  )
}

function mergePostProductionVersions(prev: PostProductionVersionRow[], next: PostProductionVersionRow[]) {
  const prevById = new Map(prev.map((v) => [v.id, v]))
  return next.map((v) => {
    const existing = prevById.get(v.id)
    return existing && isSameVersionRow(existing, v) ? existing : v
  })
}

type Props = {
  projectId: number
  shotId: number
  /** Версия видео, от которой считаются обработки (активной обработки или активное видео шота). */
  sourceVersionId: number | null
  /** Shot override; null/undefined inherits project. */
  shotFrameFormat?: string | null
  onDataChanged: () => void
  onInitialLoadSettled?: () => void
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function statusTone(status: PostProductionVersionRow["status"]) {
  switch (status) {
    case "failed":
      return "text-ui-status-failed"
    case "processing":
      return "text-ui-status-rendering"
    default:
      return "text-ui-text-muted"
  }
}

function parameterSummary(parameters: PostProductionVersionRow["parameters"]) {
  const entries = Object.entries(parameters)
  if (entries.length === 0) return "Default parameters"
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join(", ")
}

function PostProductionCard({
  row,
  busy,
  ff,
  onActivate,
  onDelete,
  overlay,
}: {
  row: PostProductionVersionRow
  busy: boolean
  ff: { aspect: string }
  onActivate: (row: PostProductionVersionRow) => void
  onDelete: (row: PostProductionVersionRow) => void
  overlay?: ReactNode
}) {
  const { t } = useTranslation("shot")
  const jobs = usePage<Partial<WorkspaceBaseProps>>().props.jobs ?? EMPTY_WORKSPACE_JOBS
  const cancelPreviewRef = useRef<() => void>(() => {})
  const canUse = row.status === "done" && row.result_shot_media_version_id != null
  const params = parameterSummary(row.parameters)
  const isPending = row.status === "processing" || row.status === "queued"
  const { thumbImg } = thumbParts(row)

  const liveDetail =
    jobs.find((j) => j.id === row.generation_job?.id)?.status_detail ?? row.generation_job?.status_detail ?? null
  const statusText = liveDetail ? statusDetailLabel(liveDetail) : t(`postProduction.status.${row.status}`)

  return (
    <li
      className={cn(
        "flex flex-col rounded-lg border dark:bg-black/25 light:bg-clay-1",
        row.active ? "border-ui-accent/70 ring-1 ring-[var(--ui-focus-ring)]/40 ring-inset" : "border-ui-border",
      )}
      onDoubleClick={() => {
        cancelPreviewRef.current()
        if (canUse && !row.active) onActivate(row)
      }}
    >
      <PostProductionPreview
        previewUrl={row.preview_url}
        stillUrl={thumbImg}
        aspectClass={ff.aspect}
        title={canUse ? t("postProduction.doubleClickUse") : undefined}
        active={row.active}
        activeLabel={t("postProduction.current")}
        cancelRef={cancelPreviewRef}
        overlay={overlay}
        empty={
          thumbImg ? (
            <img src={thumbImg} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-[10px] text-ui-text-secondary">
              {row.status === "failed" ? (
                <AlertTriangle className="h-5 w-5 text-ui-status-failed" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-ui-text-muted" />
              )}
            </div>
          )
        }
      />

      <div className="flex flex-col gap-1.5 p-2">
        <div className="flex items-center justify-between gap-1">
          <p className="text-[10px] text-ui-text-muted tabular-nums">{formatDate(row.created_at)}</p>
          {isPending ? (
            <span className={cn("text-[10px] font-semibold", statusTone(row.status))}>
              {statusText}
              <span className="ml-1 tabular-nums">{row.progress}%</span>
            </span>
          ) : row.status === "failed" ? (
            <span className={cn("text-[10px] font-semibold", statusTone(row.status))}>
              {t("postProduction.status.failed")}
            </span>
          ) : null}
        </div>

        <div className="rounded-lg border border-ui-border px-2 py-1 text-[10px] text-ui-text-secondary dark:bg-black/25 light:bg-clay-2">
          <div className="truncate" title={`${row.model.name}, ${row.model_version.api_model_code}`}>
            <span className="text-ui-text-muted">Model:</span>{" "}
            <span className="text-ui-text-primary">
              {row.model.name}, <span className="font-mono">{row.model_version.api_model_code}</span>
            </span>
          </div>
          <details className="mt-0.5">
            <summary className="cursor-pointer text-ui-accent-text hover:text-ui-accent-text">
              {t("postProduction.parameters")}
            </summary>
            <p className="mt-1 line-clamp-3 text-[10px] leading-relaxed text-ui-text-secondary">{params}</p>
          </details>
        </div>

        {isPending ? (
          <div className="h-1 overflow-hidden rounded-full bg-ui-surface-raised">
            <div className="h-full rounded-full bg-ui-accent transition-all" style={{ width: `${row.progress}%` }} />
          </div>
        ) : null}

        {row.error_message ? (
          <p className="line-clamp-2 text-[10px] leading-relaxed text-ui-status-failed">{row.error_message}</p>
        ) : null}

        <div className="flex items-center gap-1">
          <button
            type="button"
            className={cn(
              "rounded p-1 text-ui-text-secondary ring-1 ring-transparent hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:cursor-not-allowed disabled:opacity-40",
              row.active && "text-ui-status-approved",
            )}
            title={t("postProduction.doubleClickUse")}
            disabled={!canUse || row.active || busy}
            onClick={() => onActivate(row)}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-ui-text-secondary ring-1 ring-transparent hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:cursor-not-allowed disabled:opacity-40"
            title={t("common.downloadOriginal")}
            disabled={!row.download_url}
            onClick={() => row.download_url && void downloadImage(row.download_url, `post-production-${row.id}.mp4`)}
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-ui-text-secondary ring-1 ring-transparent hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:cursor-not-allowed disabled:opacity-40"
            title={t("common.delete")}
            disabled={busy}
            onClick={() => onDelete(row)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </li>
  )
}

function OriginalCard({
  row,
  busy,
  ff,
  onActivate,
  overlay,
}: {
  row: PostProductionOriginalRow
  busy: boolean
  ff: { aspect: string }
  onActivate: () => void
  overlay?: ReactNode
}) {
  const { t } = useTranslation("shot")
  const cancelPreviewRef = useRef<() => void>(() => {})
  const { thumbImg } = thumbParts(row)

  return (
    <li
      className={cn(
        "flex flex-col rounded-lg border dark:bg-black/25 light:bg-clay-1",
        row.active ? "border-ui-accent/70 ring-1 ring-[var(--ui-focus-ring)]/40 ring-inset" : "border-ui-border",
      )}
      onDoubleClick={() => {
        cancelPreviewRef.current()
        if (!row.active) onActivate()
      }}
    >
      <PostProductionPreview
        previewUrl={row.preview_url}
        stillUrl={thumbImg}
        aspectClass={ff.aspect}
        title={t("postProduction.doubleClickUse")}
        active={row.active}
        activeLabel={t("postProduction.current")}
        cancelRef={cancelPreviewRef}
        overlay={overlay}
        empty={
          thumbImg ? (
            <img src={thumbImg} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-ui-text-secondary">
              {t("postProduction.original")}
            </div>
          )
        }
      />

      <div className="flex flex-col gap-1.5 p-2">
        <div className="rounded-lg border border-ui-border px-2 py-1 text-[10px] text-ui-text-secondary dark:bg-black/25 light:bg-clay-2">
          <div className="truncate font-semibold text-ui-text-primary">{t("postProduction.original")}</div>
          <div className="truncate text-ui-text-muted">{t("postProduction.originalHint")}</div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            className={cn(
              "rounded p-1 text-ui-text-secondary ring-1 ring-transparent hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:cursor-not-allowed disabled:opacity-40",
              row.active && "text-ui-status-approved",
            )}
            title={t("postProduction.doubleClickUse")}
            disabled={row.active || busy}
            onClick={onActivate}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </button>
          {row.download_url ? (
            <button
              type="button"
              className="rounded p-1 text-ui-text-secondary ring-1 ring-transparent hover:bg-ui-surface-raised hover:text-ui-text-primary"
              title={t("common.downloadOriginal")}
              onClick={() =>
                row.download_url &&
                void downloadImage(row.download_url, `original-${row.source_shot_media_version_id}.mp4`)
              }
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>
    </li>
  )
}

export function ShotPostProductionVersionsView({
  projectId,
  shotId,
  sourceVersionId,
  shotFrameFormat,
  onDataChanged,
  onInitialLoadSettled,
}: Props) {
  const { t } = useTranslation(["shot", "common", "projects"])
  const page = usePage<Partial<WorkspaceBaseProps>>()
  const { project } = useShotPanelWorkspace()
  const jobs = page.props.jobs ?? EMPTY_WORKSPACE_JOBS
  const ff = frameFormatClasses(effectiveFrameFormat(shotFrameFormat, project?.frame_format ?? "9:16"))
  const [versions, setVersions] = useState<PostProductionVersionRow[]>([])
  const [original, setOriginal] = useState<PostProductionOriginalRow | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | "reset" | null>(null)
  const [compare, setCompare] = useState<CompareState>(null)
  const reportInitialLoad = useInitialLoadReporter(onInitialLoadSettled)

  const jobsKey = useMemo(
    () =>
      jobs
        .filter((job) => job.job_kind === "topaz_post_production" && (job.shot_ids ?? []).includes(shotId))
        .map((job) => `${job.id}:${job.status}:${job.progress}`)
        .join("|"),
    [jobs, shotId],
  )

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!sourceVersionId) {
        setVersions([])
        setOriginal(null)
        reportInitialLoad()
        return
      }
      const silent = opts.silent === true
      if (!silent) {
        setLoading(true)
        setError(null)
      }
      try {
        const data = await fetchShotPostProductionVersions(projectId, shotId, sourceVersionId)
        setVersions((prev) => mergePostProductionVersions(prev, data.versions))
        setOriginal(data.original)
        if (silent) setError(null)
      } catch (err) {
        if (!silent) {
          setError(err instanceof Error ? err.message : String(err))
          setVersions([])
          setOriginal(null)
        }
      } finally {
        if (!silent) {
          setLoading(false)
          reportInitialLoad()
        }
      }
    },
    [projectId, shotId, sourceVersionId, reportInitialLoad],
  )

  useEffect(() => {
    setCompare(null)
    void load()
  }, [load])

  // Сравнение — тот же блок, что в ленте и на «Версиях видео»: оригинал слева, обработка справа.
  const comparable = useMemo(
    () =>
      [
        original ? compareVersion(original, ORIGINAL_ID, 0, t("postProduction.original")) : null,
        ...versions
          .filter((row) => row.status === "done")
          .map((row, i) => compareVersion(row, row.id, i + 1, row.model.name)),
      ].filter((v): v is ReviewMediaVersion => v != null),
    [original, versions, t],
  )
  const compared = compare ? comparable.filter((v) => compare.ids.includes(v.id)) : []
  const frameFormat = effectiveFrameFormat(shotFrameFormat, project?.frame_format ?? "9:16")

  // Polling job-задач не должен показывать спиннер — обновляем карточки на месте.
  useEffect(() => {
    if (!jobsKey) return
    void load({ silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- триггер только на изменение статуса/прогресса задач
  }, [jobsKey])

  const activeJob = useMemo(() => activeShotJob(jobs, shotId, POST_PRODUCTION_JOB_KINDS), [jobs, shotId])

  const handleJobFinished = useCallback(async () => {
    void load({ silent: true })
    onDataChanged()
    router.reload({ only: ["tree"] })
  }, [load, onDataChanged])

  useReloadOnJobFinish(activeJob, handleJobFinished)

  const handleActivate = async (row: PostProductionVersionRow) => {
    setBusyId(row.id)
    try {
      await activatePostProductionVersion(projectId, shotId, row.id)
      setVersions((list) => list.map((item) => ({ ...item, active: item.id === row.id })))
      setOriginal((orig) => (orig ? { ...orig, active: false } : orig))
      onDataChanged()
      router.reload({ only: ["tree"] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const handleReset = async () => {
    if (!sourceVersionId) return
    setBusyId("reset")
    try {
      await resetPostProductionVersions(projectId, shotId, sourceVersionId)
      setVersions((list) => list.map((item) => ({ ...item, active: false })))
      setOriginal((orig) => (orig ? { ...orig, active: true } : orig))
      onDataChanged()
      router.reload({ only: ["tree"] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (row: PostProductionVersionRow) => {
    if (!(await confirm({ message: t("postProduction.deleteConfirm"), tone: "danger" }))) return
    setBusyId(row.id)
    try {
      await deletePostProductionVersion(projectId, shotId, row.id)
      setVersions((list) => list.filter((item) => item.id !== row.id))
      onDataChanged()
      router.reload({ only: ["tree"] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-start gap-3 px-4 pb-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-ui-text-primary">{t("postProduction.panelTitle")}</h2>
          <p className="mt-0.5 text-xs text-ui-text-muted">
            {compare
              ? t("projects:review.compare.hint")
              : sourceVersionId
                ? t("postProduction.panelHintWithSource", { id: sourceVersionId })
                : t("postProduction.panelHint")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3 pt-0.5">
          <VersionCompareControls
            compare={compare}
            startIds={comparable.slice(0, 2).map((v) => v.id)}
            onCompareChange={setCompare}
          />
        </div>
      </div>
      <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto pt-3">
        {!sourceVersionId ? (
          <p className="py-8 text-center text-sm text-ui-text-muted">{t("postProduction.noSource")}</p>
        ) : loading ? (
          <p className="py-8 text-center text-sm text-ui-text-muted">{t("common.loading")}</p>
        ) : error ? (
          <p className="py-4 text-center text-sm text-ui-status-failed">{error}</p>
        ) : (
          <>
            {compare && compared.length === 2 ? (
              <div className="mx-1 mb-3 border-b border-ui-border pb-3">
                <VersionCompare a={compared[0]} b={compared[1]} mode={compare.mode} frameFormat={frameFormat} />
              </div>
            ) : null}
            <ul className="grid grid-cols-2 gap-3 p-1 sm:grid-cols-3">
              {versions.map((row) => (
                <PostProductionCard
                  key={row.id}
                  row={row}
                  busy={busyId === row.id}
                  ff={ff}
                  onActivate={(item) => void handleActivate(item)}
                  onDelete={(item) => void handleDelete(item)}
                  overlay={
                    row.status === "done" && row.preview_url ? (
                      <VersionCompareSlot compare={compare} id={row.id} onCompareChange={setCompare} />
                    ) : null
                  }
                />
              ))}
              {original ? (
                <OriginalCard
                  row={original}
                  busy={busyId === "reset"}
                  ff={ff}
                  onActivate={() => void handleReset()}
                  overlay={
                    original.preview_url ? (
                      <VersionCompareSlot compare={compare} id={ORIGINAL_ID} onCompareChange={setCompare} />
                    ) : null
                  }
                />
              ) : null}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
